import { isAxiosError } from "axios";
import { ApiErrorCode } from "config/errorCodes";
import type { BudgetCategory } from "./types";

export const CATEGORY_NOT_REGENERABLE_REASON = "AI can't regenerate this category yet.";

const NO_CHARGE_NOTE = "No credits were used.";

/**
 * The BUDGET_RERUN_DISALLOWED refusals the category picker explains inline, by the API's
 * `reason_code`. The API `message` is written for the connector and is never matched or shown
 * here. A refusal without one of these codes keeps the normal error toast.
 */
const REFUSAL_COPY_BY_REASON_CODE: Readonly<Record<string, string>> = {
  no_room: "These categories need more room under your maximum. Raise the maximum or pick fewer.",
  not_regenerable: CATEGORY_NOT_REGENERABLE_REASON,
  duplicate_category_name: CATEGORY_NOT_REGENERABLE_REASON,
  categories_changed: "These categories changed. Refresh the budget and pick again.",
};

/** The refusal body: from the axios error, or the body itself on the client's refresh path. */
function refusalBody(error: unknown): { status: number; body: Record<string, unknown> } | null {
  if (isAxiosError(error)) {
    const data = error.response?.data;
    if (!error.response || !data || typeof data !== "object") return null;
    return { status: error.response.status, body: data as Record<string, unknown> };
  }
  if (error && typeof error === "object" && !(error instanceof Error)) {
    const body = error as Record<string, unknown>;
    return typeof body.status_code === "number" ? { status: body.status_code, body } : null;
  }
  return null;
}

/**
 * Picker text when the backend refused a targeted rerun before holding credits: no room under
 * the maximum, or a category AI cannot regenerate. Null for any other error.
 */
export function targetedRerunRefusalMessage(error: unknown): string | null {
  const refusal = refusalBody(error);
  if (!refusal || refusal.status < 400 || refusal.status >= 500) return null;
  const { code, error_code: errorCode, reason_code: reasonCode } = refusal.body;
  if ((code ?? errorCode) !== ApiErrorCode.BUDGET_RERUN_DISALLOWED) return null;
  const copy = typeof reasonCode === "string" ? REFUSAL_COPY_BY_REASON_CODE[reasonCode] : undefined;
  return copy ? `${copy} ${NO_CHARGE_NOTE}` : null;
}

/**
 * The top-level categories AI can regenerate in a targeted rerun. The only place that reads the
 * backend's `rerun_eligibility`. The rest are never offered, so the picker shows no dead options.
 * Budgets without the field predate it and count as regenerable.
 */
export function regenerableCategories(categories: BudgetCategory[]): BudgetCategory[] {
  return categories.filter((category) => category.rerun_eligibility?.regenerable !== false);
}

export const TARGETED_RERUN_KEEP_NOTE =
  "Every other category keeps its line items exactly as they are.";

/** "Update 1 category", "Update 3 categories", or "Update categories" before any pick. */
export function targetedRerunActionLabel(selectedCount: number): string {
  if (selectedCount === 0) return "Update categories";
  return `Update ${selectedCount} ${selectedCount === 1 ? "category" : "categories"}`;
}

const MAX_NAMED_CATEGORIES = 3;

function joinWithAnd(parts: string[]): string {
  if (parts.length <= 1) return parts.join("");
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/** "Updated Production and Post Production. Other line items unchanged." Past 3: "and N more". */
export function targetedRerunDoneMessage(categoryNames: string[]): string {
  const named = categoryNames.slice(0, MAX_NAMED_CATEGORIES);
  const rest = categoryNames.length - named.length;
  const list = rest > 0 ? `${named.join(", ")} and ${rest} more` : joinWithAnd(named);
  return `Updated ${list}. Other line items unchanged.`;
}
